import { IRuntimeConfig, RUNTIME_CONFIG_SCHEMA } from "src/types/runtime-config";
import { getEnvVar } from "src/utils/resolve-env-var";

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        __ENV?: IRuntimeConfig;
    }
}

const EMPTY_RUNTIME_CONFIG: IRuntimeConfig = Object.fromEntries(
    Object.entries(RUNTIME_CONFIG_SCHEMA).map(([key, { type }]) => [key, type === "boolean" ? false : ""])
) as IRuntimeConfig;

function buildServerConfig(): IRuntimeConfig {
    return Object.fromEntries(
        Object.entries(RUNTIME_CONFIG_SCHEMA).map(([key, { envVar, type }]) => {
            const raw = getEnvVar(envVar);
            return [key, type === "boolean" ? raw.toLowerCase() === "true" : raw];
        })
    ) as IRuntimeConfig;
}

/**
 * Returns the runtime configuration.
 *
 * On the server (SSR): reads from process.env directly — _document.tsx injects
 * the same values into window.__ENV so the client receives them before hydration.
 *
 * On the client: reads from window.__ENV which was injected by _document.tsx
 * as an inline <script> before React hydrates, so values are always available
 * on first render with no loading race.
 */
export function getRuntimeConfig(): IRuntimeConfig {
    if (globalThis.window === undefined) {
        return buildServerConfig();
    }

    // Client — globalThis.window.__ENV injected by _document.tsx before React hydrates
    return globalThis.window.__ENV ?? EMPTY_RUNTIME_CONFIG;
}

/**
 * Reads a single runtime config value.
 *
 * Convenience wrapper around getRuntimeConfig() for call sites that only need one key.
 * Reads from window.__ENV on the client (injected by _document.tsx before hydration)
 * and from process.env on the server.
 */
export function getRuntimeConfigValue<K extends keyof IRuntimeConfig>(key: K): IRuntimeConfig[K] {
    return getRuntimeConfig()[key];
}
